import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/** "Add as a new release" or "Merge into an existing song" (design catalog-merge c2). */
export type ClaimWay = 'new' | 'merge';

/** The query parameters of the claim dialog on the Catalog page. */
export const claimRouteParams = { claim: 'claim', way: 'way', into: 'into' } as const;

/** Set on the history entry that the dialog pushed, so the close goes Back instead of adding an entry. */
interface ClaimRouteState {
  claimPushed?: boolean;
}

function parseWay(value: string | null): ClaimWay | undefined {
  return value === 'new' || value === 'merge' ? value : undefined;
}

export interface ClaimRoute {
  /** The `catalogTrackMaid` of the suggestion that the dialog is open on. */
  claimMaid: string | undefined;
  /** Undefined when the address names no way: the dialog picks the default. */
  way: ClaimWay | undefined;
  /** The picked row of the merge. */
  intoUuid: string | undefined;
  open: (catalogTrackMaid: string) => void;
  close: () => void;
  setWay: (way: ClaimWay) => void;
  setInto: (uuid: string | undefined) => void;
}

/**
 * The address of the claim dialog (`?claim=<maid>&way=merge&into=<uuid>`). Reload and a shared
 * link open the same state; Back closes the dialog. A change inside the dialog replaces the
 * history entry, so one Back always closes it.
 */
export function useClaimRoute(): ClaimRoute {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? null) as ClaimRouteState | null;
  const claimMaid = params.get(claimRouteParams.claim) ?? undefined;
  const way = parseWay(params.get(claimRouteParams.way));
  const intoUuid = params.get(claimRouteParams.into) ?? undefined;

  const open = useCallback(
    (catalogTrackMaid: string) => {
      const next = new URLSearchParams(params);
      next.set(claimRouteParams.claim, catalogTrackMaid);
      next.delete(claimRouteParams.way);
      next.delete(claimRouteParams.into);
      setParams(next, { state: { claimPushed: true } satisfies ClaimRouteState });
    },
    [params, setParams]
  );

  const close = useCallback(() => {
    if (state?.claimPushed === true) {
      // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
      void navigate(-1);
      return;
    }
    const next = new URLSearchParams(params);
    Object.values(claimRouteParams).forEach((key) => next.delete(key));
    setParams(next, { replace: true });
  }, [navigate, params, setParams, state?.claimPushed]);

  const replaceParam = useCallback(
    (key: string, value: string | undefined) => {
      const next = new URLSearchParams(params);
      if (value === undefined) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      setParams(next, { replace: true, state });
    },
    [params, setParams, state]
  );
  const setWay = useCallback((value: ClaimWay) => replaceParam(claimRouteParams.way, value), [replaceParam]);
  const setInto = useCallback((uuid: string | undefined) => replaceParam(claimRouteParams.into, uuid), [replaceParam]);

  return useMemo(
    () => ({ claimMaid, way, intoUuid, open, close, setWay, setInto }),
    [claimMaid, close, intoUuid, open, setInto, setWay, way]
  );
}
